import React, { useMemo, useState } from 'react';

import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { useSearchFlashOffers } from '~/hooks/apis/offers/useSearchFlashOffers';
import useDebounced from '~/hooks/useDebounced';

import { hotelOnSaleRegions, regionName } from './regions';

function onSaleLabel(offer: App.Offer) {
  const regions = hotelOnSaleRegions(offer);
  return regions.length > 0 ? `On sale in ${regions.map(regionName).join(', ')}` : 'Not on sale now';
}

interface Props {
  value: App.Offer | null;
  onChange: (offer: App.Offer | null) => void;
}

export default function FlashOfferPicker({ value, onChange }: Props) {
  const [inputValue, setInputValue] = useState('');

  const search = useDebounced(inputValue.trim(), SEARCH_DEBOUNCE_MS);

  const { data: offers = [], isFetching } = useSearchFlashOffers(search);

  // Only hotels customers can buy now can be bundled. The selection stays in the list so the
  // field does not clear itself as the search changes.
  const onSale = useMemo(() => offers.filter((offer) => hotelOnSaleRegions(offer).length > 0), [offers]);
  const options = useMemo(
    () =>
      !value || onSale.some((option) => option.id_salesforce_external === value.id_salesforce_external)
        ? onSale
        : [value, ...onSale],
    [onSale, value],
  );
  const hiddenCount = offers.length - onSale.length;

  return (
    <Autocomplete
      value={value}
      onChange={(_event, offer) => onChange(offer)}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      options={options}
      getOptionLabel={(option) => `${option.name} (${option.id_salesforce_external})`}
      isOptionEqualToValue={(option, selected) => option.id_salesforce_external === selected.id_salesforce_external}
      filterOptions={(all) => all}
      loading={isFetching}
      noOptionsText={
        hiddenCount > 0
          ? `No hotels on sale match "${search}". ${hiddenCount} off sale ${hiddenCount === 1 ? 'is' : 'are'} hidden.`
          : search
            ? `No flash hotels match "${search}"`
            : 'No flash hotels found'
      }
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props}>
          <Box>
            <Typography variant="body2">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.id_salesforce_external} · {onSaleLabel(option)}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Flash hotel"
          helperText="Hotels on sale now. Search by name or offer id."
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
